import { Link } from "react-router-dom";
import {
  ArrowRight, Star, Award, Baby, Sparkles, Stethoscope,
  HeartPulse, Flower2, HandHeart, Phone, Quote, MapPin,
  ChevronRight, ShieldCheck, User, Zap, Heart
} from "lucide-react";
import Placeholder from "@/components/Placeholder";
import {
  Reveal, StaggerGroup, StaggerItem, Magnetic, Counter,
  WordReveal, ShinyText, FloatingBlob, GlowCard, AnimatedLine, ParallaxSection
} from "@/components/Motion";
import { services, testimonials, facilities } from "@/lib/data";
import { SITE, whatsappLink } from "@/lib/api";
import SEO from "@/components/SEO";

const iconMap = { Baby, Sparkles, Stethoscope, HeartPulse, Flower2, HandHeart };

export default function Home() {
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "MedicalClinic",
    "name": "WellMed Hospitals",
    "image": `${SITE.domain}/wellmed-hospital.webp`,
    "description": "Premium obstetrics, gynaecology, and women's health hospital in Gurugram.",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "242, Sector 39",
      "addressLocality": "Gurugram",
      "addressRegion": "Haryana",
      "postalCode": "122003",
      "addressCountry": "IN"
    },
    "telephone": SITE.phone,
    "url": SITE.domain,
    "medicalSpecialty": ["Obstetrics", "Gynecologic"],
    "isAcceptingNewPatients": true,
  };

  return (
    <main data-testid="home-page">
      <SEO title="Dr. Priyanka Gupta | Expert Women's Healthcare" schema={localBusinessSchema} />

      {/* ── HERO ─────────────────────────────────────────────── */}
      <section className="relative overflow-hidden min-h-[100svh] flex items-center bg-[var(--color-bg)] pt-12 lg:pt-0">
        
        {/* Mobile Hero (matches reference image) */}
        <div className="block lg:hidden max-w-md mx-auto px-4 pt-0 pb-12 w-full text-center">
          {/* Image Container */}
          <Reveal className="relative mb-8">
            <div className="relative w-full aspect-[4/5] rounded-[2rem] overflow-hidden bg-gray-200">
              <Placeholder variant="hero" label="Dr. Priyanka Portrait" Icon={User} testId="hero-portrait-mobile" src="/images/facilities/hero-screenshot.png" className="object-top" />
            </div>

            {/* Dark rosette badge (bottom-right overlap) */}
            <div className="absolute -bottom-3 right-0 bg-[var(--color-text)] text-white w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg border-[3px] border-[var(--color-bg)] z-10">
              <Award className="w-7 h-7" />
            </div>

            {/* White rating pill (bottom-center overlap) */}
            <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 bg-white px-5 py-2.5 rounded-full shadow-[0_8px_24px_rgba(0,0,0,0.08)] border border-[var(--color-border)] flex items-center gap-2 whitespace-nowrap z-10">
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-[var(--color-coral)] text-[var(--color-coral)]" />
                ))}
              </div>
              <span className="font-semibold text-[var(--color-text)] text-[clamp(11px,3vw,14px)]">4.8/5</span>
              <span className="text-[clamp(10px,2.5vw,14px)] text-[var(--color-text-faint)]">from 500+ patients</span>
            </div>
          </Reveal>

          {/* Text Content */}
          <Reveal delay={0.2}>
            
            <p className="text-[clamp(14px,4vw,18px)] text-[var(--color-text)] mb-3 font-medium text-balance">
              20 years of clinical excellence <span className="text-[var(--color-coral)] mx-1 font-bold">•</span> Over 20,000 mothers guided
            </p>
            
            <p className="text-[clamp(13px,3.5vw,16px)] text-[var(--color-text-muted)] flex flex-wrap items-center justify-center gap-1 text-balance">
              <MapPin className="w-4 h-4 text-[var(--color-coral)] shrink-0" />
              <span>Previously worked in <span className="font-semibold text-[var(--color-text)]">Delhi, Bangalore, Vadodara & Gurugram</span></span>
            </p>
            
            {/* CTA Buttons for mobile */}
            <div className="mt-8 flex flex-col gap-3">
              <Link to="/appointment" className="btn-primary w-full justify-center text-sm py-3.5">
                Book an Appointment
              </Link>
              <a href={whatsappLink()} target="_blank" rel="noreferrer" className="btn-outline w-full justify-center text-sm py-3.5 bg-white border-[var(--color-border)]">
                Chat on WhatsApp
              </a>
            </div>
          </Reveal>
        </div>

        {/* Desktop Hero */}
        <div className="hidden lg:grid max-w-7xl mx-auto px-6 lg:px-8 pt-8 pb-16 lg:pt-10 lg:pb-32 lg:grid-cols-12 gap-8 lg:gap-16 items-center relative w-full">
          <div className="lg:col-span-7">

            <Reveal>
              <div className="overline mb-6 inline-flex items-center gap-2">
                <span className="w-6 h-px bg-[var(--color-coral)]" />
                Gold Medalist · Gurugram
              </div>
            </Reveal>

            <h1 className="font-heading text-4xl md:text-6xl lg:text-[5.5rem] text-[var(--color-text)] leading-[1.02] tracking-tight text-balance">
              <WordReveal text="Expert care for" />
              <br />
              <WordReveal text="your" delay={0.15} />
              {" "}
              <span className="text-[var(--color-coral)] italic font-light">
                <WordReveal text="journey" delay={0.3} />
              </span>
              {" "}
              <WordReveal text="to" delay={0.42} />
              <br />
              <WordReveal text="motherhood." delay={0.5} />
            </h1>

            <Reveal delay={0.6}>
              <p className="mt-8 text-base md:text-lg text-[var(--color-text-muted)] max-w-xl leading-relaxed text-pretty">
                20 years of clinical excellence. Over 20,000 mothers guided across Delhi,
                Vadodara, Bengaluru and Gurugram. Patient-first care, delivered with empathy
                and surgical precision.
              </p>
            </Reveal>

            <Reveal delay={0.75}>
              <div className="mt-9 flex flex-wrap items-center gap-4">
                <Magnetic>
                  <Link to="/appointment" data-testid="hero-book-btn" className="btn-primary">
                    Book an Appointment <ArrowRight className="w-4 h-4" />
                  </Link>
                </Magnetic>
                <a
                  href={whatsappLink()}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="hero-whatsapp-btn"
                  className="btn-outline"
                >
                  Chat on WhatsApp
                </a>
              </div>
            </Reveal>

            {/* STATS */}
            <StaggerGroup className="mt-10 lg:mt-14 grid grid-cols-3 gap-2 sm:gap-4 max-w-lg" stagger={0.1}>
              {[
                { num: 20, suffix: "+", label: "Years" },
                { num: 20000, suffix: "+", label: "Mothers Guided" },
                { num: 4.8, suffix: "★", label: "500+ Reviews", isFloat: true },
              ].map((s) => (
                <StaggerItem key={s.label} className="h-full">
                  <div className="stat-card text-center h-full flex flex-col justify-center items-center p-3 sm:p-4">
                    <div className="font-heading text-2xl sm:text-3xl md:text-4xl text-[var(--color-text)]">
                      {s.isFloat
                        ? <span>4.8★</span>
                        : <Counter to={s.num} suffix={s.suffix} />}
                    </div>
                    <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--color-text-faint)] mt-2 font-medium">
                      {s.label}
                    </div>
                  </div>
                </StaggerItem>
              ))}
            </StaggerGroup>

          </div>

          {/* HERO IMAGE */}
          <Reveal delay={0.3} className="lg:col-span-5 relative">
            <div className="relative img-hover-scale">
              <Placeholder variant="hero" label="Dr. Priyanka Portrait" Icon={User} testId="hero-portrait" src="/images/facilities/hero-screenshot.png" className="object-[center_10%]" />
            </div>
          </Reveal>
        </div>

        {/* Bottom gradient fade */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[var(--color-bg)] to-transparent pointer-events-none" />
      </section>

      {/* ── MEET DOCTOR ──────────────────────────────────────── */}
      <section className="py-12 lg:py-32 relative overflow-hidden bg-gradient-to-b from-[var(--color-bg)] to-[var(--color-bg-alt)]">
        <FloatingBlob color="aurora-warm" size={400} top="20%" right="-8%" delay={1} />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-14 items-center relative">
          <Reveal className="lg:col-span-5">
            <div className="img-hover-scale relative">
              <Placeholder variant="tall" label="Doctor in Practice" Icon={User} testId="about-portrait" src="/images/facilities/m2.webp" />
              {/* Decorative corner accent */}
              <div className="absolute -bottom-4 -right-4 w-24 h-24 rounded-2xl border-2 border-[var(--color-coral)]/30 pointer-events-none" />
            </div>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-7">
            <div className="overline mb-5">Meet Your Doctor</div>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl text-[var(--color-text)] leading-[1.1] text-balance">
              Dr. Priyanka Gupta —{" "}
              <em className="text-[var(--color-coral)] not-italic">Two decades</em>{" "}
              dedicated to women&apos;s health.
            </h2>

            <p className="mt-6 text-base md:text-lg text-[var(--color-text-muted)] leading-relaxed text-pretty">
              MBBS, MD (Obstetrics &amp; Gynaecology) — Gold Medalist. Fellow in Laparoscopic
              Surgery. Co-Founder &amp; Director of Obstetrics, Gynaecology &amp; Women&apos;s
              Health at WellMed Hospitals.
            </p>

            <div className="mt-8 grid sm:grid-cols-2 gap-x-10 gap-y-3">
              {[
                "High-risk Pregnancy", "Advanced Laparoscopy",
                "Infertility & IVF", "Hormonal Disorders",
                "Women's Wellness", "Adolescent Care",
              ].map((s) => (
                <div key={s} className="flex items-center gap-3 text-sm text-[var(--color-text-muted)]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[var(--color-coral)] shrink-0" />
                  {s}
                </div>
              ))}
            </div>

            {/* Quote block */}
            <blockquote className="mt-9 p-6 glass-card relative">
              <Quote className="absolute -top-3 -left-3 w-7 h-7 text-[var(--color-coral)] fill-[var(--color-coral)]" />
              <p className="font-heading italic text-lg md:text-xl text-white leading-relaxed text-pretty">
                &ldquo;The journey of motherhood is sacred. Every woman deserves the highest standard
                of care, delivered with empathy and respect.&rdquo;
              </p>
              <footer className="mt-3 text-xs uppercase tracking-wider text-[var(--color-coral)] font-semibold">
                <cite>— Dr. Priyanka Gupta</cite>
              </footer>
            </blockquote>

            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/about" data-testid="home-about-link" className="btn-outline text-sm">
                Learn More <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/appointment" data-testid="home-book-link" className="btn-primary text-sm">
                Book Consultation
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <AnimatedLine className="mx-6 lg:mx-16" />

      {/* ── SERVICES ─────────────────────────────────────────── */}
      <section className="py-12 lg:py-32 relative">
        <FloatingBlob color="aurora-coral" size={350} bottom="10%" left="-5%" delay={3} />

        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-16">
              <div className="max-w-2xl">
                <div className="overline mb-4">Comprehensive Care</div>
                <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl text-[var(--color-text)] leading-[1.1] text-balance">
                  <ShinyText>Specialized services for every stage of your journey.</ShinyText>
                </h2>
              </div>
            </div>
          </Reveal>

          <StaggerGroup className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-16 mt-8" stagger={0.07}>
            {services.map((s, i) => {
              const I = iconMap[s.icon] || HeartPulse;
              return (
                <StaggerItem key={s.slug}>
                  <Link
                    to={`/services/${s.slug}`}
                    data-testid={`service-card-${s.slug}`}
                    className="group block"
                  >
                    <div className="border-t border-[var(--color-border)] pt-6 h-full">
                      <div className="flex items-center justify-between mb-4">
                        <I className="w-6 h-6 text-[var(--color-text)] group-hover:text-[var(--color-coral)] transition-colors" strokeWidth={1.5} />
                        <ArrowRight className="w-4 h-4 text-[var(--color-text-faint)] group-hover:text-[var(--color-coral)] transition-colors" />
                      </div>

                      <h3 className="font-heading text-2xl text-[var(--color-text)] mb-3 group-hover:text-[var(--color-coral)] transition-colors">{s.title}</h3>
                      <p className="text-sm text-[var(--color-text-muted)] leading-relaxed mb-5 text-pretty">{s.short}</p>

                      <ul className="space-y-2">
                        {s.bullets.slice(0, 2).map((b) => (
                          <li key={b} className="text-xs text-[var(--color-text-muted)] flex items-center gap-2">
                            <span className="w-1 h-1 rounded-full bg-[var(--color-text-faint)] shrink-0" /> {b}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </Link>
                </StaggerItem>
              );
            })}
          </StaggerGroup>
        </div>
      </section>

      {/* ── WHY CHOOSE ───────────────────────────────────────── */}
      <section className="py-12 lg:py-32 bg-[var(--color-bg)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-16">
              <div className="overline mb-4">Excellence In Care</div>
              <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl text-[var(--color-text)] leading-[1.1] text-balance">
                Why families trust{" "}
                <span className="text-[var(--color-coral)] italic">Dr. Priyanka.</span>
              </h2>
            </div>
          </Reveal>

          <StaggerGroup className="grid md:grid-cols-2 lg:grid-cols-4 gap-x-10 gap-y-12 mt-12" stagger={0.1}>
            {[
              { num: "01", title: "20+ Years Excellence", desc: "Gold Medalist with experience across Delhi, Vadodara, Bengaluru and Gurugram." },
              { num: "02", title: "20,000+ Mothers Guided", desc: "Successful pregnancies, fertility journeys and surgical care." },
              { num: "03", title: "Premium Facility", desc: "WellMed Hospitals — NABH-accredited, world-class infrastructure." },
              { num: "04", title: "Holistic Approach", desc: "Medical excellence combined with emotional support and warmth." },
            ].map((f) => (
              <StaggerItem key={f.title}>
                <div className="flex flex-col h-full border-l-2 border-[var(--color-coral)]/20 pl-6">
                  <div className="font-heading text-4xl text-[var(--color-coral)]/40 mb-4">{f.num}</div>
                  <h3 className="font-heading text-xl text-[var(--color-text)] mb-2">{f.title}</h3>
                  <p className="text-sm text-[var(--color-text-muted)] leading-relaxed text-pretty">{f.desc}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      <AnimatedLine className="mx-6 lg:mx-16" delay={0.2} />

      {/* ── WELLMED TEASER ───────────────────────────────────── */}
      <section className="py-12 lg:py-32 relative overflow-hidden">
        <FloatingBlob color="aurora-coral" size={380} top="30%" right="-8%" delay={2} />

        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-14 items-center relative">
          <Reveal className="lg:col-span-6">
            <div className="overline mb-4">World-Class Healthcare</div>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl text-[var(--color-text)] leading-[1.1] text-balance">
              WellMed Hospitals —{" "}
              <em className="text-[var(--color-coral)] not-italic">a vision realized.</em>
            </h2>
            <p className="mt-6 text-base md:text-lg text-[var(--color-text-muted)] leading-relaxed text-pretty">
              Co-founded by Dr. Priyanka Gupta, WellMed is a 50-bed NABH-accredited
              multi-specialty hospital combining world-class medical care with deep
              compassion — in a patient-friendly environment.
            </p>

            <StaggerGroup className="mt-8 grid grid-cols-2 gap-3" stagger={0.06}>
              {facilities.slice(0, 4).map((f) => (
                <StaggerItem key={f.title}>
                  <div className="glass-card px-4 py-3 hover:border-[var(--color-coral)]/40 transition-colors">
                    <div className="text-xs text-white font-semibold uppercase tracking-wider">{f.title}</div>
                  </div>
                </StaggerItem>
              ))}
            </StaggerGroup>

            <div className="mt-8 flex flex-wrap gap-4">
              <Magnetic>
                <Link to="/wellmed" data-testid="home-wellmed-link" className="btn-outline text-sm">
                  Visit WellMed <ArrowRight className="w-4 h-4" />
                </Link>
              </Magnetic>
              <a href={`tel:${SITE.phone}`} data-testid="home-call-link" className="btn-ghost text-sm">
                <Phone className="w-4 h-4" /> {SITE.phoneDisplay}
              </a>
            </div>
          </Reveal>

          <Reveal delay={0.2} className="lg:col-span-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="img-hover-scale rounded-2xl">
                <Placeholder variant="tall" label="Hospital Exterior" src="/images/facilities/DSC04279.webp" />
              </div>
              <div className="grid grid-rows-2 gap-4">
                <div className="img-hover-scale rounded-2xl">
                  <Placeholder variant="square" label="Modular OT" src="/images/facilities/DSC04315.webp" />
                </div>
                <div className="img-hover-scale rounded-2xl">
                  <Placeholder variant="square" label="NICU" src="/images/facilities/DSC04318.webp" />
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── TESTIMONIALS ─────────────────────────────────────── */}
      <section className="py-12 lg:py-32 relative overflow-hidden">
        <FloatingBlob color="aurora-warm" size={400} top="-5%" left="-8%" delay={1} />

        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-16">
              <div className="max-w-2xl">
                <div className="overline mb-4">Patient Reviews</div>
                <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl text-[var(--color-text)] leading-[1.1]">
                  4.8★ from{" "}
                  <em className="text-[var(--color-coral)] not-italic">500+ patients.</em>
                </h2>
              </div>
              <Link to="/testimonials" data-testid="home-reviews-link" className="btn-ghost text-sm">
                Read all reviews <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>

          <StaggerGroup className="grid md:grid-cols-2 lg:grid-cols-3 gap-10 mt-12" stagger={0.1}>
            {testimonials.slice(0, 3).map((t, i) => (
              <StaggerItem key={i}>
                <div className="h-full flex flex-col justify-between">
                  <div>
                    {/* Stars */}
                    <div className="flex items-center gap-1 mb-6">
                      {[...Array(5)].map((_, j) => (
                        <Star key={j} className="w-4 h-4 fill-[var(--color-coral)] text-[var(--color-coral)]" />
                      ))}
                    </div>

                    <p className="text-lg md:text-xl text-[var(--color-text)] leading-relaxed font-heading italic text-pretty">
                      &ldquo;{t.text}&rdquo;
                    </p>
                  </div>

                  <div className="mt-8 pt-5 border-t border-[var(--color-border)] flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[var(--color-surface-2)] border border-[var(--color-border)] flex items-center justify-center text-[var(--color-text)] font-heading text-lg">
                      {t.name[0]}
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-[var(--color-text)]">{t.name}</div>
                      <div className="text-[10px] uppercase tracking-wider text-[var(--color-text-faint)] mt-0.5">Verified Patient</div>
                    </div>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* ── FINAL CTA ─────────────────────────────────────────── */}
      <section className="py-12 lg:py-32">
        <aside className="max-w-5xl mx-auto px-6 lg:px-8">
          <Reveal>
            <div className="relative overflow-hidden rounded-3xl">
              {/* BG */}
              <div className="absolute inset-0 bg-gradient-to-br from-[var(--color-coral)] via-[var(--color-coral-deep)] to-[oklch(40%_0.18_38)]" />

              {/* Decorative orbs inside CTA - Removed to humanize layout */}
              <div className="relative p-10 lg:p-16">
                <div className="overline text-white/80 mb-4">Ready when you are</div>
                <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl text-white leading-[1.05] text-balance">
                  Begin your healthcare journey, today.
                </h2>
                <p className="mt-5 text-white/75 max-w-xl text-lg leading-relaxed text-pretty">
                  Book a private consultation at WellMed Hospitals, Sector 39, Gurugram.
                  Mornings 11–2, evenings 6–8.
                </p>

                <div className="mt-8 flex flex-wrap gap-4">
                  <Magnetic>
                    <Link
                      to="/appointment"
                      data-testid="cta-book-btn"
                      className="inline-flex items-center gap-2 bg-white text-[var(--color-coral-deep)] rounded-full px-8 py-4 font-semibold text-sm hover:bg-white/90 hover:-translate-y-1 transition-all duration-300 shadow-[0_8px_24px_rgba(0,0,0,0.2)]"
                    >
                      Book Appointment <ArrowRight className="w-4 h-4" />
                    </Link>
                  </Magnetic>
                  <a
                    href={`tel:${SITE.phone}`}
                    className="inline-flex items-center gap-2 border border-white/40 text-white rounded-full px-8 py-4 font-medium text-sm hover:bg-white/10 transition-all duration-300"
                  >
                    <Phone className="w-4 h-4" /> {SITE.phoneDisplay}
                  </a>
                </div>

                <div className="mt-8 flex items-center gap-3 text-sm text-white/60">
                  <MapPin className="w-4 h-4 text-white shrink-0" /> {SITE.address}
                </div>
              </div>
            </div>
          </Reveal>
        </aside>
      </section>

    </main>
  );
}
